import { Component, OnInit, Input, EventEmitter, Output, inject, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { ActivatedRoute, Router, RouterLinkActive, RouterLink, RouterOutlet } from "@angular/router";

import { AdjustmentArchive } from "../vibe-history/vibe-history.component";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities, RECENT_OPERATIONS_LIMIT } from "src/app/dvs-utilities";
import { AdjDefLabelPipe, DatetimePipe, OperationLabelPipe, OperationsLimitNotePipe } from "src/app/pipes";
import { AdjustmentGridComponent } from "src/app/shared/adjustment-grid/adjustment-grid.component";
import { RegimeListComponent } from "src/app/shared/regime-list/regime-list.component";
import { ShaftOrderComponent } from "src/app/shared/shaft-order/shaft-order.component";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-rotor-vibe",
	templateUrl: "./rotor-vibe.component.html",
	styleUrls: ["./rotor-vibe.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormsModule, DvsChartComponent, RouterLinkActive, RouterLink, RouterOutlet, DatetimePipe, AdjDefLabelPipe, OperationLabelPipe, OperationsLimitNotePipe, RegimeListComponent, ShaftOrderComponent, AdjustmentGridComponent]
})
export class RotorVibeComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	readonly RECENT_OPERATIONS_LIMIT = RECENT_OPERATIONS_LIMIT;

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;
	@Output()
	recordAdjustment = new EventEmitter<AdjustmentArchive>();

	@ViewChild("crtAdjForm") crtAdjForm: NgForm;
	@ViewChild("adjustmentForm") adjustmentForm: NgForm;

	public GetOpClass = DvsUtilities.GetOpClass;
	public operations: APIResults.Operation[];
	public multiOps = false;
	public operation?: APIResults.Operation;
	public multiOperation?: APIResults.Operation[];
	public regimeCounts: APIResults.RegimeCount[];
	public selectedRegimes: number[];
	public adjustments?: number[][] | string[][];
	public alternates?: number[][][] | string[][][];
	public h60Prediction?: number[];
	public h60PredictionAlternates?: number[][];
	public shaftOrder = 1;
	public adjustmentSelect: boolean[];
	public vshafts?: VShaftWithPlot[];
	public errormessage?: string;
	public onePerRevOnly = true;
	public isH60 = false;
	public crtAdj: string[];

	public trackOptions: Highcharts.Options;

	public adjDetail: boolean[];

	ngOnInit() {
		if (!this.rotor || !this.rotor.vshaft) return;
		this.vshafts = this.rotor.vshaft;
		this.isH60 = this.rotor.balancedata.shaftbalance?.toUpperCase() === "H60";
		if (this.isH60) {
			this.crtAdj = Array(this.rotor.blade.length).fill("0");
		}

		// Pick a chart tab if none selected or route is invalid
		const routeVshaftId = this.route?.firstChild?.snapshot?.params["vshaftid"];
		if (!routeVshaftId || !this.vshafts.find(item => item.id === routeVshaftId)) {
			let defaultVshaft = this.vshafts[0]?.id;
			const findDefault = this.vshafts.find(item => item.default);
			if (findDefault) defaultVshaft = findDefault.id;
			this.router.navigate([defaultVshaft], { relativeTo: this.route, replaceUrl: true });
		}
		this.adjustmentSelect = Array(this.rotor.balancedata.numadjustmenttypes).fill(true);
		this.adjDetail = Array(this.rotor.balancedata.numadjustmenttypes).fill(false);
		this.refreshOperations();
	}

	rotorChange() {
		this.ngOnInit();
	}

	async refreshOperations() {
		this.operations = undefined;
		this.operation = undefined;
		this.regimeCounts = undefined;
		const operations = await this.api.GetRTBOperations(this.asset.asset_id, this.rotor.rotorid, false, RECENT_OPERATIONS_LIMIT);
		this.operations = operations;
		if (this.operations.length > 0) {
			let selectedOp: APIResults.Operation;
			// Currently tries to select first op with rotor turn >= 5 minutes
			if (!BrandVars.fixedassets) {
				selectedOp = this.operations.find(item => item.rotor_turn_time > 300);
			}
			if (!selectedOp) {
				selectedOp = this.operations[0];
			}
			this.operation = selectedOp;
			this.multiOperation = [selectedOp];
			await this.refreshRegimeCounts();
		}
	}

	/** Must fire when an operation is selected to get the counts */
	async refreshRegimeCounts() {
		if (!this.multiOps) {
			if (this.operation && this.rotor) {
				this.regimeCounts = undefined;
				const counts = await this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, this.operation.operation_id);
				// Populate regime names
				if (counts && counts.vibe) {
					for (const regime of counts.vibe) {
						regime.name = this.regimeConfigs[regime.regime_id].name;
						regime.evaluate = this.regimeConfigs[regime.regime_id].default;
					}
					// If no regimes are defaulted on, turn on the last one
					if (!counts.vibe.find(regime => regime.evaluate)) counts.vibe[counts.vibe.length - 1].evaluate = true;
					this.regimeCounts = counts.vibe;
				}
			}
		} else {
			if (this.multiOperation && this.rotor) {
				// Can only select 5 operations
				if (this.multiOperation.length > 5) {
					this.multiOperation = this.multiOperation.slice(0, 5);
				}
				this.regimeCounts = undefined;
				const builder = [];
				for (const op of this.multiOperation) {
					const counts = await this.api.GetRegimeCounts(this.asset.asset_id, this.rotor.rotorid, op.operation_id);
					// Populate regime names, sum up counts
					if (counts && counts.vibe) {
						for (const regime of counts.vibe) {
							let match = builder.find(item => item.regime_id === regime.regime_id);
							if (!match) {
								match = { regime_id: regime.regime_id, count: 0 };
								match.name = this.regimeConfigs[regime.regime_id].name;
								match.evaluate = this.regimeConfigs[regime.regime_id].default;
								builder.push(match);
							}
							match.count += regime.count;
						}
					}
				}
				// If no regimes are defaulted on, turn on the last one
				if (!builder.find(regime => regime.evaluate)) builder[builder.length - 1].evaluate = true;
				this.regimeCounts = builder;
			}
		}
		this.regimesChange();
	}

	regimesChange() {
		if (this.regimeCounts) {
			this.selectedRegimes = this.regimeCounts.filter(item => item.evaluate).map(item => item.regime_id);
			this.updatePlot();
		}
	}

	async updatePlot() {
		this.errormessage = undefined;
		if (this.isH60 && this.crtAdjForm?.invalid) {
			this.errormessage = "Only values of 0, L, H, LL, LH, HH are allowed for adjustments in H60 mode";
			return;
		}

		this.adjustments = undefined;
		this.alternates = undefined;
		this.h60Prediction = undefined;
		this.h60PredictionAlternates = undefined;
		this.trackOptions = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				let results: any;
				let ops = [this.operation?.operation_id];
				if (this.multiOps) ops = this.multiOperation.map(item => item.operation_id);
				if (this.isH60) {
					this.crtAdj = this.crtAdj.map(a => a.toUpperCase());
					results = await this.api.GetRTBH60(this.asset.asset_id, this.rotor.rotorid, ops, this.crtAdj);
					const baseRegime = results.data[Object.keys(results.data)[0]].regimes[Object.keys(results.data[Object.keys(results.data)[0]].regimes)[0]];
					if (baseRegime && baseRegime.prediction) {
						this.h60Prediction = baseRegime.prediction;
						this.h60PredictionAlternates = results.predictionAlternates;
					}
				} else {
					results = await this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, ops, this.selectedRegimes, this.shaftOrder, this.adjustmentSelect, false, this.onePerRevOnly);
				}
				this.fillPlot(results);
				this.fillPlotTrack(results);
				if (this.isH60) {
					this.adjustments = results.adjustments || null;
				} else {
					this.adjustments = results.adjustments.map(item => item.map(cell => Number(cell.toFixed(1))));
				}
				this.alternates = results.alternates;
			} catch (err) {
				if (err?.message === "Empty data") {
					this.errormessage = "Couldn't load RTB data\nThe selected regime(s) may not be valid for 2/rev analysis";
				} else if (err?.error?.message === "H60 data must contain exactly one regime") {
					this.errormessage = "H60 data must contain exactly one regime";
				} else {
					console.log(err);
					this.errormessage = "Couldn't load RTB data";
				}
			}
		}
	}

	SetAdjustment(adjIdx: number, altIdx: number) {
		const tmpAdj = this.adjustments[adjIdx];
		this.adjustments[adjIdx] = this.alternates[adjIdx][altIdx];
		this.alternates[adjIdx][altIdx] = tmpAdj;
		this.adjDetail[adjIdx] = false;
		if (this.isH60 && this.h60Prediction && this.h60PredictionAlternates) {
			const tmpPred = this.h60PredictionAlternates[altIdx];
			this.h60PredictionAlternates[altIdx] = this.h60Prediction;
			this.h60Prediction = tmpPred;
			if (this.activeRouteComponent?.UpdatePrediction) this.activeRouteComponent.UpdatePrediction(this.h60Prediction);
		}
	}

	async ManualAdjustment() {
		this.errormessage = undefined;
		if (this.isH60 && this.adjustmentForm?.invalid) {
			this.errormessage = "Only values of 0, L, H, LL, LH, HH are allowed for adjustments in H60 mode";
			return;
		}

		this.alternates = undefined;
		this.h60PredictionAlternates = undefined;
		if (this.selectedRegimes && this.selectedRegimes.length) {
			if (this.vshafts) {
				for (const vshaft of this.vshafts) {
					vshaft.plotOptions = undefined;
				}
			}
			try {
				let results: any;
				if (this.isH60) {
					this.adjustments[0] = this.adjustments[0].map(a => a.toUpperCase());
					results = await this.api.GetRTBH60(this.asset.asset_id, this.rotor.rotorid, [this.operation.operation_id], this.crtAdj, this.adjustments[0] as string[]);
				} else {
					results = await this.api.GetRTBData(this.asset.asset_id, this.rotor.rotorid, [this.operation.operation_id], this.selectedRegimes, this.shaftOrder, this.adjustmentSelect, false, this.onePerRevOnly, this.adjustments as number[][]);
				}
				this.fillPlot(results);
				this.fillPlotTrack(results);
			} catch (err) {
				console.log(err);
				this.errormessage = "Couldn't load RTB data";
			}
		}
	}

	private fillPlot(rtbResults: APIResults.RTBCommon) {
		if (this.vshafts) {
			for (const vshaft of this.vshafts) {
				const comp = rtbResults.data[vshaft.shftid];
				if (comp) {
					const series: Highcharts.SeriesScatterOptions[] = [];
					for (const regimeKey of Object.keys(comp.regimes)) {
						const regime = this.regimeCounts.find(item => item.regime_id === Number(regimeKey));
						const regimedata = comp.regimes[regimeKey];
						const points: any[] = [];
						if (regimedata.data.length === 0) throw new Error("Empty data");
						// Offset phase by vshaft.rotate if available
						points.push(...regimedata.data.map(point => [DvsUtilities.AddDegreeOffset(point[0], vshaft.rotate), point[1]]));
						if (regimedata.prediction && regimedata.prediction.length >= 2) {
							points.push({
								x: DvsUtilities.AddDegreeOffset(regimedata.prediction[0], vshaft.rotate),
								y: regimedata.prediction[1],
								name: "Prediction",
								marker: { symbol: "diamond", radius: 7, lineColor: "white", lineWidth: 2 },
							});
						}
						points.push({
							x: DvsUtilities.AddDegreeOffset(regimedata.average[0], vshaft.rotate),
							y: regimedata.average[1],
							name: "Average",
							marker: { symbol: "square", radius: 5, lineColor: "white", lineWidth: 2 },
						});
						series.push({
							name: regime.name,
							type: "scatter",
							data: points,
							marker: { symbol: "circle", radius: 4 },
						});
					}
					vshaft.plotOptions = this.charting.RTBScatterDefaults(series);
				} else {
					vshaft.plotOptions = undefined;
				}
			}
		}
	}

	private fillPlotTrack(rtbResults: APIResults.RTBResults) {
		if (rtbResults.track === undefined || Object.keys(rtbResults.track).length === 0) return;
		const series: Highcharts.SeriesScatterOptions[] = [];
		for (const regimeKey of Object.keys(rtbResults.track)) {
			const regime = this.regimeCounts.find(item => item.regime_id === Number(regimeKey));
			const regimedata = rtbResults.track[regimeKey];
			const points: any[] = [];
			points.push(...regimedata.predtrack.map((value, index) => {
				return {
					x: index,
					y: value, name: "Predicted Track",
					marker: { symbol: "diamond", radius: 7, lineColor: "white", lineWidth: 2 },
				};
			}));
			points.push(...regimedata.track.map((value, index) => {
				return {
					x: index,
					leadlag: regimedata.leadlag[index],
					y: value, name: "Current Track",
					marker: { symbol: "square", radius: 5, lineColor: "white", lineWidth: 2 },
				};
			}));
			series.push({
				name: regime.name,
				type: "scatter",
				data: points,
				marker: { symbol: "circle", radius: 4 },
			});
		}
		const trackUnits = this.rotor.inches ? "in" : "mm";
		this.trackOptions = this.charting.RTBTrackDefaults(series, this.ReorderBlades(this.rotor.blade).map(item => item.name), trackUnits);
	}


	RecordAdjustment() {
		const archive: AdjustmentArchive = {
			operation_id: this.operation.operation_id,
			operation_start: this.operation.start_time,
			adjustments: this.adjustments,
			isClockwise: false,
		};
		this.recordAdjustment.emit(archive);
	}

	// Note, this is only used to show the bonus track chart (if present), not the adjustments
	public ReorderBlades(filtered: any[]) {
		if (this.rotor.balancedata.clockwise && filtered.length > 1) {
			// Reorder blades if clockwise, see email "sorry, learned something new" 9/24/2025
			const reordered = filtered.slice().reverse();
			reordered.unshift(reordered.pop());
			return reordered;
		}
		return filtered;
	}

	private activeRouteComponent: any;
	public updateRouterOutlet(routecomponent?: any) {
		if (routecomponent) {
			this.activeRouteComponent = routecomponent;
		}
		if (this.activeRouteComponent) {
			const vshaftid = this.route?.firstChild?.snapshot?.params["vshaftid"];
			const vshaft = this.vshafts.find(item => item.id === vshaftid);
			this.activeRouteComponent.vshaft = vshaft;
		}
	}
}

interface VShaftWithPlot extends APIResults.VShaft {
	plotOptions?: Highcharts.Options;
}
